import { useAuth } from './useAuth';
import { RolePermissions, type Permission } from '@jerico/shared';
import type { UserRole } from '@jerico/shared';

/**
 * Returns true if the current user has the given permission.
 */
export function useHasPermission(permission: Permission): boolean {
  const { user } = useAuth();
  if (!user) return false;
  const role = user.role as UserRole;
  const perms = RolePermissions[role];
  if (!perms) return false;
  return perms.includes(permission);
}

/**
 * Returns true if the current user has ALL of the given permissions.
 */
export function useHasAllPermissions(...permissions: Permission[]): boolean {
  const { user } = useAuth();
  if (!user) return false;
  const role = user.role as UserRole;
  const perms = RolePermissions[role];
  if (!perms) return false;
  return permissions.every((p) => perms.includes(p));
}

/**
 * Returns true if the current user has ANY of the given permissions.
 */
export function useHasAnyPermission(...permissions: Permission[]): boolean {
  const { user } = useAuth();
  if (!user) return false;
  const role = user.role as UserRole;
  const perms = RolePermissions[role];
  if (!perms) return false;
  return permissions.some((p) => perms.includes(p));
}

/**
 * Returns true if the current user is a system admin.
 */
export function useIsSystemAdmin(): boolean {
  const { user } = useAuth();
  return user?.role === 'system_admin';
}

/**
 * Returns true if the current user is an owner or admin of their tenant.
 */
export function useIsTenantAdmin(): boolean {
  const { user } = useAuth();
  return user?.role === 'owner' || user?.role === 'admin' || user?.role === 'system_admin';
}
